"use strict";
exports.__esModule = true;
exports.utmGrabber = void 0;
var defaultNullValue = "[not provided]";
/**
 * Main function to start the UTM capturing process.
 * It does four things:
 *   1. Sets the session_referrer (and session_referrer_created_at) cookie when a new session starts (using rolling expiry).
 *   2. Processes the visitor's document referrer and sets related UTM cookies.
 *   3. Extracts UTM parameters from the URL and stores them as cookies.
 *   4. Records the date when UTM info was first captured.
 */
function utmGrabber() {
    handleSessionReferrerCookies(); // Handles session-based tracking and sets session_referrer, session_referrer_details and session_referrer_created_at cookies
    handleReferrerCookies(); // Generates referrer and referrer_details cookies, not scoped to the active session (these are 'last touch')
    handleDocumentReferrer(); // Handles setting UTM campaign cookies based on the document.referrer, organic search traffic is handled here
    handleUTMGrabbing(); // Looks for UTM campaiogn parameters in the URL and sets cookies
    handleSetFirstKnownUtmDate(); // Records the first visit date when UTM info was captured.
}
exports.utmGrabber = utmGrabber;
/**
 *
 * Helper function to detecting internal referrals
 */
function isInternalReferral(referrer) {
    return referrer.includes(window.location.hostname);
}
/**
 * Helper function that handles generating the referrer & referrer_details values
 *
 */
function handleReferrerValues() {
    var referrerValue = "";
    var referrerDetailsValue = "";
    var urlParams = new URLSearchParams(window.location.search);
    /* HANDLE REFERRER COOKIE VALUE */
    // Check for a utm_source query param value, if it exists, set the referrer value to it
    var utmSource = urlParams.get("utm_source");
    if (utmSource) {
        referrerValue = utmSource;
    }
    // Check for a "referrer" query parameter in the URL - if it exists, use it
    // the referrer query param will take priority over the utm_source
    var referrerUrlParam = urlParams.get("referrer");
    if (referrerUrlParam) {
        referrerValue = referrerUrlParam;
    }
    // If no query parameter exists, check for document.referrer value
    // if exists - set referrer value to 'referral'
    var documentReferrer = document.referrer;
    var isInternal = isInternalReferral(documentReferrer);
    if (!referrerValue && !isInternal && documentReferrer) {
        referrerValue = "referral";
    }
    if (referrerValue === "") {
        referrerValue = "direct";
    }
    /* HANDLE REFERRER_DETAILS COOKIE VALUE */
    // Check for utm_campaign param in url, if it exists, use it
    var utmCampaign = urlParams.get("utm_campaign");
    if (utmCampaign) {
        referrerDetailsValue = utmCampaign;
    }
    // Check for referrer_details param in url, if it exists, use it
    var referrerDetailsUrlParam = urlParams.get("referrer_details");
    if (referrerDetailsUrlParam) {
        referrerDetailsValue = referrerDetailsUrlParam;
    }
    // If no query parameter exists, fall back to document.referrer.
    if (!referrerDetailsValue) {
        referrerDetailsValue = document.referrer;
    }
    // If still no referrer details, set it to 'direct'
    if (referrerDetailsValue === "") {
        referrerDetailsValue = "direct";
    }
    return {
        referrer: referrerValue,
        referrerDetails: referrerDetailsValue
    };
}
/**
 * Determines the cookie domain.
 * If the current hostname includes "apollographql.com", sets the cookie domain accordingly.
 * Otherwise, returns an empty string.
 */
function getDomain() {
    return window.location.hostname.includes("apollographql.com")
        ? "domain=apollographql.com;"
        : "";
}
// Expiration times (in seconds)
var expireTime = 60 * 60 * 24 * 30; // 30 days for regular UTM cookies.
var firstKnownExpireTime = 34560000; // 400 days.
var sessionExpireSeconds = 5 * 24 * 60 * 60; // 5 days for rolling session expiry.
// Cookie name for storing the date of the first UTM capture.
var firstKnownUtmDateCookieName = "first_known_utm_date";
/**
 * List of UTM parameters we care about.
 */
var utmParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_icid",
    "utm_ICID",
    "gclid",
];
/**
 * List of recognized search engines.
 */
var searchEngines = [
    { name: "google", url: "http://www.google.com", source: "google" },
    { name: "bing", url: "http://www.bing.com", source: "bing" },
    { name: "yahoo", url: "http://www.yahoo.com", source: "yahoo" },
    { name: "duckduckgo", url: "https://duckduckgo.com/", source: "duckduckgo" },
    { name: "baidu", url: "http://www.baidu.com", source: "baidu" },
    { name: "yandex", url: "http://www.yandex.com", source: "yandex" },
    { name: "ecosia", url: "https://www.ecosia.org/", source: "ecosia.org" },
];
/**
 * Sets a cookie with a given name, value, and max-age (in seconds).
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {number} maxAgeSeconds - The expiration time in seconds.
 */
function setCookie(name, value, maxAgeSeconds) {
    document.cookie = "".concat(name, "=").concat(value, "; samesite=lax; path=/; ").concat(getDomain(), " max-age=").concat(maxAgeSeconds);
}
/**
 * Retrieves the value of a cookie by its name.
 * @param {string} name - The name of the cookie.
 * @returns {string|null} - The cookie's value or null if not found.
 */
function getCookie(name) {
    var _a;
    var nameEQ = "".concat(name, "=");
    var ca = document.cookie.split(";");
    for (var i = 0; i < ca.length; i++) {
        var c = (_a = ca[i]) === null || _a === void 0 ? void 0 : _a.trim();
        if ((c === null || c === void 0 ? void 0 : c.indexOf(nameEQ)) === 0)
            return c.substring(nameEQ.length);
    }
    return null;
}
function noUtmCookiesExist() {
    return !utmParams.some(function (param) { return getCookie(param); });
}
/**
 * Deletes a cookie by setting its expiration date to a past date.
 * @param {string} name - The name of the cookie to delete.
 */
function deleteCookie(name) {
    document.cookie = "".concat(name, "=; path=/; samesite=lax; ").concat(getDomain(), " expires=Thu, 01 Jan 1970 00:00:01 GMT;");
}
/**
 * Handles the session_referrer using rolling session expiry.
 * The session is tracked in sessionStorage so that it naturally ends when the tab is closed.
 * The session is considered expired if the last recorded timestamp is older than 5 days.
 *
 * On a new session:
 *   - Checks for a "referrer" query parameter in the URL.
 *   - If absent, falls back to document.referrer.
 *   - If the referrer is empty, sets it to "direct".
 *   - If a non-internal referrer exists, sets the session_referrer cookie and a session_referrer_timestamp cookie.
 *
 * On every page load within an active session, the session_start timestamp is updated.
 */
function handleSessionReferrerCookies() {
    var now = Date.now();
    var sessionExpireMs = sessionExpireSeconds * 1000;
    var storedSessionStart = sessionStorage.getItem("session_start");
    var _a = handleReferrerValues(), referrer = _a.referrer, referrerDetails = _a.referrerDetails;
    var activeSessionExists = storedSessionStart &&
        now - parseInt(storedSessionStart, 10) < sessionExpireMs;
    if (activeSessionExists) {
        sessionStorage.setItem("session_start", now.toString());
        return;
    }
    // We only want to proceed with session_referrer logic if no active session exists
    // this is to ensure we don't overwrite the session_referrer/session_referrer_details
    // if the session is still active
    // New session: record the current timestamp.
    sessionStorage.setItem("session_start", now.toString());
    // Only set session_referrer if it exists and is not an internal referral.
    if (!referrer.includes(window.location.hostname)) {
        setCookie("session_referrer", referrer, sessionExpireSeconds);
        // Set session_referrer_details cookie
        setCookie("session_referrer_details", referrerDetails, sessionExpireSeconds);
        // Set session_referrer_timestamp cookie to the current timestamp.
        setCookie("session_referrer_created_at", now.toString(), sessionExpireSeconds);
    }
}
/**
 * Handles setting the referrer and referrer_details cookies.
 */
function handleReferrerCookies() {
    var _a = handleReferrerValues(), referrer = _a.referrer, referrerDetails = _a.referrerDetails;
    // Set referrer and referrer_details cookies
    setCookie("referrer", referrer, expireTime);
    setCookie("referrer_details", referrerDetails, expireTime);
}
/**
 * Processes the document referrer and sets related UTM cookies.
 */
function handleDocumentReferrer() {
    var referrer = document.referrer;
    // If there's no referrer, treat the traffic as direct.
    if (!referrer || referrer === "" || referrer === "undefined") {
        if (noUtmCookiesExist()) {
            setCookie("utm_source", "direct", expireTime);
            setCookie("utm_medium", "website", expireTime);
            // set remaining UTM param values to default value
            utmParams.forEach(function (param) {
                if (param !== "utm_medium" && param !== "utm_source") {
                    setCookie(param, defaultNullValue, expireTime);
                }
            });
        }
        if (!getCookie("first_known_utm_date")) {
            setCookie("first_known_utm_source", "direct", firstKnownExpireTime);
            setCookie("first_known_utm_medium", "website", firstKnownExpireTime);
        }
        return;
    }
    // Ignore internal referrers.
    if (isInternalReferral(referrer)) {
        return;
    }
    // Delete existing UTM cookies to avoid conflicts.
    utmParams.forEach(function (param) {
        if (getCookie(param)) {
            deleteCookie(param);
        }
    });
    // Check if the referrer matches any known search engine.
    var matchedSearchEngine = searchEngines.filter(function (engine) {
        return referrer.includes(engine.name);
    });
    var firstKnownUtmDate = getCookie(firstKnownUtmDateCookieName);
    var searchEngine = matchedSearchEngine.at(0);
    if (searchEngine) {
        // For search engine traffic, set medium as "organic".
        setCookie("utm_medium", "organic", expireTime);
        setCookie("utm_source", searchEngine.source, expireTime);
        if (!firstKnownUtmDate) {
            setCookie("first_known_utm_medium", "organic", firstKnownExpireTime);
            setCookie("first_known_utm_source", searchEngine.source, firstKnownExpireTime);
        }
        // set remaining UTM param values to default value
        utmParams.forEach(function (param) {
            if (param !== "utm_medium" && param !== "utm_source") {
                setCookie(param, defaultNullValue, expireTime);
            }
        });
    }
    else {
        // For other referrals, treat the medium as "referral".
        setCookie("utm_medium", "referral", expireTime);
        setCookie("utm_source", referrer, expireTime);
        if (!firstKnownUtmDate) {
            setCookie("first_known_utm_medium", "referral", firstKnownExpireTime);
            setCookie("first_known_utm_source", referrer, firstKnownExpireTime);
        }
        // set remaining UTM param values to default value
        utmParams.forEach(function (param) {
            if (param !== "utm_medium" && param !== "utm_source") {
                setCookie(param, defaultNullValue, expireTime);
            }
        });
    }
}
/**
 * Extracts UTM parameters from the URL and stores them as cookies.
 */
function handleUTMGrabbing() {
    var url = new URL(window.location.href);
    var utmsExist = false;
    var firstKnownUtmDate = getCookie(firstKnownUtmDateCookieName);
    // run a check to see if there are any utms present in the url
    utmParams.forEach(function (param) {
        var pValue = url.searchParams.get(param);
        if (pValue !== null && pValue !== "" && pValue !== undefined) {
            utmsExist = true;
        }
    });
    if (!utmsExist) {
        return;
    }
    // we know there are at least one expected utm param(s) in the url
    // proceed with setting the utm cookies
    utmParams.forEach(function (param) {
        var pValue = url.searchParams.get(param);
        if (pValue !== null && pValue !== "" && pValue !== undefined) {
            deleteCookie(param);
            setCookie(param, pValue, expireTime);
            if (!firstKnownUtmDate) {
                setCookie("first_known_".concat(param), pValue, firstKnownExpireTime);
            }
        }
        else {
            // handle setting of default utm param value for the remaining utm params
            deleteCookie(param);
            setCookie(param, defaultNullValue, expireTime);
        }
    });
}
/**
 * Records the date and time of the first visit where UTM information was captured.
 */
function handleSetFirstKnownUtmDate() {
    if (!getCookie(firstKnownUtmDateCookieName)) {
        setCookie(firstKnownUtmDateCookieName, new Date().toISOString(), firstKnownExpireTime);
    }
}
